/* =========================
   PANEL ANIMATIONS (v3)
   Każdy panel ma GSAP timeline odpalany na evencie panel:enter
   (dispatched przez swiper-engine.js). Reverse przy panel:leave.
   Tylko desktop (>= 1024px) — eventy są dispatched tylko gdy Swiper
   aktywny (mount tylko desktop), więc dodatkowy guard niepotrzebny.
========================= */

(function () {
  const timelines = new Map(); // panelName → { tl, played }
  // Wystawiamy mapę globalnie żeby gsap.js (createTweenFlip) mógł odpalić
  // concept timeline w synchronizacji ze starter fadeout (przed Swiper mount).
  window.__panelTimelines = timelines;

  const DUR = 0.7;
  const EASE = 'power3.out';

  /**
   * Budowniczowie timeline'ów per panel.
   * Każdy zwraca paused gsap.timeline.
   * Wywoływane raz przy pierwszym wejściu na panel (lazy build).
   */
  const builders = {
    concept(panel) {
      // Sekwencja: 1) title fade-in/scale, 2) desc w górę + cta w dół (równolegle),
      // 3) aside wsuwa się z prawej.
      const tl = gsap.timeline({ paused: true, defaults: { duration: DUR, ease: EASE } });
      const title = panel.querySelector('.concept__title');
      const desc = panel.querySelector('.concept__desc');
      const cta = panel.querySelector('.concept__cta--wrapper');
      const aside = panel.querySelector('.concept__aside');

      if (title) {
        tl.from(title, { scale: 0.85, autoAlpha: 0, duration: 0.9, ease: 'power3.out' }, 0);
      }
      // desc wyjeżdża do góry (start poniżej swojej pozycji, ląduje na miejscu)
      if (desc) {
        tl.from(desc, { y: 60, autoAlpha: 0 }, 0.6);
      }
      // cta wyjeżdża w dół (start powyżej swojej pozycji) — równolegle z desc
      if (cta) {
        tl.from(cta, { y: -40, autoAlpha: 0 }, 0.6);
      }
      // aside wsuwa się z prawej — po desc/cta
      if (aside) {
        tl.from(aside, { x: 80, autoAlpha: 0, ease: 'back.out(1.4)', duration: 0.8 }, 1.0);
      }

      return tl;
    },

    portfolio(panel) {
      // 3-fazowa entry:
      //   1) title (z lewej, kolumna grid 1) + items (z prawej) wjeżdżają
      //   2) title leci za lewą krawędź
      //   3) kolumna title kurczy się do 0 → items zajmują pełną szerokość
      const tl = gsap.timeline({ paused: true, defaults: { duration: DUR, ease: EASE } });
      const container = panel.querySelector('.portfolio .container');
      const titleWrap = panel.querySelector('.portfolio__title-wrap');
      const items = panel.querySelectorAll('.portfolio__item');
      const filters = panel.querySelector('.portfolio__filters:not(.mobile)');

      // Faza 1: entry — title z lewej, items z prawej (stagger)
      if (titleWrap) {
        tl.fromTo(titleWrap, { x: -80, autoAlpha: 0 }, { x: 0, autoAlpha: 1, duration: 0.8 }, 0);
      }
      if (items.length) {
        // BEZ stagger — wszystkie items wjeżdżają razem. Stagger powodował
        // wizualnie progresywne opacity dalszych itemów gdy timeline odgrywany
        // wielokrotnie (Flip filtering + powrót na panel).
        tl.fromTo(items, { x: 120, autoAlpha: 0 }, { x: 0, autoAlpha: 1, duration: 0.6 }, 0.1);
      }

      // Faza 2: title slide-out + kolumna grid 1 kurczy się do 0
      // (grid-template-columns z "auto 100%" na "0px 100%" — Chrome/Safari/FF
      // wspierają interpolację grid templates od ~2023). items zajmują pełną
      // szerokość automatycznie.
      if (titleWrap) {
        tl.to(titleWrap, { xPercent: -120, autoAlpha: 0, duration: 0.7, ease: 'power2.in' }, 1.4);
      }
      if (container) {
        tl.to(
          container,
          { gridTemplateColumns: '0px 100%', duration: 0.7, ease: 'power2.inOut' },
          1.6,
        );
      }

      // Faza 3: filtry pojawiają się na końcu
      if (filters) {
        tl.fromTo(filters, { y: 30, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.5 }, 2.0);
      }

      return tl;
    },

    trust(panel) {
      // CAŁY stan logosów (x, y, rotation, autoAlpha) kontroluje trustScroller
      // w swiper-engine.js — nie ruszamy ich tu, żeby nie nadpisać gsap.set.
      const tl = gsap.timeline({ paused: true, defaults: { duration: 0.5, ease: EASE } });
      const title = panel.querySelector('.trust__title');
      if (title) tl.from(title, { y: -30, autoAlpha: 0, duration: 0.7 }, 0);
      return tl;
    },

    passion(panel) {
      // 2-fazowa entry: najpierw title + desc (z lekkim staggerem), DOPIERO POTEM
      // wjeżdża video z prawej. Wzorowane na oryginalnej wersji (web_id).
      const tl = gsap.timeline({ paused: true, defaults: { duration: DUR, ease: EASE } });
      const title = panel.querySelector('.passion__title');
      const desc = panel.querySelector('.passion__desc') || panel.querySelector('.passion__text');
      const video = panel.querySelector('.passion__video-wrap') || panel.querySelector('.passion__video');
      if (title) tl.from(title, { yPercent: 100, autoAlpha: 0, duration: 0.9 }, 0);
      if (desc) tl.from(desc, { y: 30, x: -20, autoAlpha: 0 }, 0.25);
      // video wjeżdża z prawej dopiero gdy title/desc są już na ekranie
      if (video) tl.from(video, { x: 200, autoAlpha: 0, duration: 1.0, ease: 'power2.out' }, 1.0);
      return tl;
    },

    awards(panel) {
      const tl = gsap.timeline({ paused: true, defaults: { duration: DUR, ease: EASE } });
      const title = panel.querySelector('.awards__title');
      const rows = panel.querySelectorAll('.awards__row');
      const logosGrid = panel.querySelector('.awards__logos-grid');
      const logos = panel.querySelectorAll('.awards__logos-grid img, .awards__logos-grid > *');
      // SCSS trzyma .awards__title, .awards__row, .awards__logos-grid na opacity:0 (laptop).
      // `from()` animowałby z 0 do bieżącego CSS state (czyli też 0) — używamy fromTo.
      if (title) {
        tl.fromTo(
          title,
          { y: -40, x: 20, autoAlpha: 0, clipPath: 'inset(0 0 100% 0)' },
          { y: 0, x: 0, autoAlpha: 1, clipPath: 'inset(0 0 0% 0)', duration: 0.8 },
          0,
        );
      }
      if (rows.length) {
        tl.fromTo(
          rows,
          { x: -50, autoAlpha: 0 },
          { x: 0, autoAlpha: 1, stagger: 0.1 },
          0.2,
        );
      }
      if (logosGrid) {
        tl.fromTo(logosGrid, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.4 }, 0.4);
      }
      if (logos.length) {
        tl.fromTo(
          logos,
          { y: -40, autoAlpha: 0 },
          { y: 0, autoAlpha: 0.6, stagger: 0.05 },
          0.4,
        );
      }
      return tl;
    },

    contact(panel) {
      const tl = gsap.timeline({ paused: true, defaults: { duration: 0.9, ease: 'power2.out' } });
      // NIE animujemy root .panel (Swiper zarządza jego transform).
      // Targetujemy dziecko .contact lub wszystkie .container'y wewnątrz.
      const inner = panel.querySelector('.contact') || panel.querySelector('.container') || panel.firstElementChild;
      if (inner) {
        tl.from(inner, { filter: 'blur(20px)', y: 30, autoAlpha: 0 }, 0);
      }
      return tl;
    },

    offer(panel) {
      // Wrapper shrink jak w oryginale (web_id): video-wrapper ma 120vh/100vw
      // w SCSS — bez scale wypełnia ekran. Po wejściu na panel skalujemy do 0.55
      // żeby video było widoczne jako prostokąt w centrum (z border-radius).
      const tl = gsap.timeline({ paused: true, defaults: { duration: 0.9, ease: 'power3.inOut' } });
      const wrapper = panel.querySelector('.offer__video-wrapper');
      if (wrapper) {
        // SCSS używa transform: translate(-50%, -50%) na wrapperze — GSAP nadpisałby
        // całe transform, więc reprodukujemy translate przez xPercent/yPercent.
        tl.fromTo(
          wrapper,
          { xPercent: -50, yPercent: -50, scale: 1, borderRadius: 0 },
          { xPercent: -50, yPercent: -50, scale: 0.55, borderRadius: '1rem' },
          0,
        );
      }
      return tl;
    },
  };

  function getOrBuild(panelName, slideEl) {
    if (timelines.has(panelName)) return timelines.get(panelName);
    const builder = builders[panelName];
    if (!builder) {
      timelines.set(panelName, null);
      return null;
    }
    const tl = builder(slideEl);
    const entry = { tl, played: false };
    timelines.set(panelName, entry);
    return entry;
  }

  const ENTER_DELAY_MS = 200;

  function onEnter(e) {
    const { panelName } = e.detail;
    if (!panelName) return;
    const swiper = window.__horizontalSwiper;
    if (!swiper) return;
    const slideEl = swiper.slides[e.detail.index];
    if (!slideEl) return;

    const entry = getOrBuild(panelName, slideEl);
    if (!entry) return;
    entry.tl.timeScale(1).pause(0);
    setTimeout(() => entry.tl.play(0), ENTER_DELAY_MS);
    entry.played = true;
  }

  function onLeave(e) {
    const { panelName } = e.detail;
    if (!panelName) return;
    const entry = timelines.get(panelName);
    if (!entry || !entry.tl) return;
    // Reverse rapid (2x faster) — żeby panel "schował się" zanim wjedzie kolejny
    entry.tl.timeScale(2).reverse();
  }

  function init() {
    if (typeof gsap === 'undefined') return;
    // panel:enter (slideChangeTransitionStart) + delay 0.2s w onEnter — entry
    // timeline startuje gdy slide jest mniej-więcej na ekranie ale przed pełnym
    // końcem transition. Czeka na panel:enter:end byłoby ~0.8s = za późno.
    document.addEventListener('panel:enter', onEnter);
    document.addEventListener('panel:leave', onLeave);

    // Concept entry jest odpalany przez gsap.js (createTweenFlip) w pozycji tflPos
    // — patrz window.__playConceptEntry. Nie potrzebujemy fallbacku na
    // starter:finished — flag `played` chroni przed podwójnym odpaleniem.
  }

  // Globalny helper — wywoływany przez gsap.js (createTweenFlip) w momencie
  // gdy logo SLATE zaczyna jechać w górę (pozycja 14 master timeline'u).
  // Timing kontrolowany przez ScrollTrigger, więc tutaj odpalamy timeline od razu.
  let conceptPendingTimer = 0;
  window.__playConceptEntry = function () {
    const slideEl = document.querySelector('.panel--concept');
    if (!slideEl) return;
    const entry = getOrBuild('concept', slideEl);
    if (!entry || entry.played) return;
    entry.played = true;
    if (conceptPendingTimer) { clearTimeout(conceptPendingTimer); conceptPendingTimer = 0; }
    entry.tl.timeScale(1).play(0);
  };

  // Cofa concept entry — wywoływane gdy user scrolluje wstecz do startera
  // (ScrollTrigger onEnterBack w gsap.js). Resetuje stan żeby kolejne wejście
  // na concept (przez tflPos) odpaliło animację od początku.
  window.__reverseConceptEntry = function () {
    const slideEl = document.querySelector('.panel--concept');
    if (!slideEl) return;
    const entry = timelines.get('concept');
    if (!entry || !entry.tl) return;
    if (conceptPendingTimer) {
      clearTimeout(conceptPendingTimer);
      conceptPendingTimer = 0;
    }
    entry.tl.timeScale(2).reverse();
    entry.played = false;
  };

  // Init na DOMContentLoaded (po dostępności gsap globalu)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
